import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, fmtError } from "../../lib/api";
import { useSite } from "../../context/SiteContext";
import { Switch } from "../ui/switch";
import { FieldInput } from "./FieldInput";

export const SETTINGS_SECTIONS = {
  association: {
    label: "About Association",
    fields: [
      { name: "association_name", label: "Association name" },
      { name: "logo", label: "Logo", type: "image" },
      { name: "established", label: "Established (year) – leave empty to hide" },
      { name: "location", label: "Location" },
      { name: "about_intro", label: "Introduction", type: "textarea", rows: 3 },
      { name: "history", label: "History", type: "textarea", rows: 6 },
      { name: "mission", label: "Mission", type: "textarea", rows: 3 },
      { name: "vision", label: "Vision", type: "textarea", rows: 3 },
      { name: "objectives", label: "Objectives (one per line)", type: "textarea", rows: 6 },
      { name: "development_activities", label: "Player development activities", type: "textarea", rows: 3 },
      { name: "coaching_programmes", label: "Coaching programmes", type: "textarea", rows: 3 },
      { name: "competitive_pathway", label: "Competitive pathway", type: "textarea", rows: 3 },
      { name: "community_development", label: "Community development", type: "textarea", rows: 3 },
    ],
  },
  contact: {
    label: "Contact Information",
    fields: [
      { name: "association_name", label: "Association name" },
      { name: "address", label: "Office address", type: "textarea", rows: 2 },
      { name: "phone", label: "Mobile number" },
      { name: "email", label: "Email address" },
      { name: "website_url", label: "Website URL" },
      { name: "office_hours", label: "Office working hours" },
      { name: "map_query", label: "Google Maps location (address or place name) – used when no embed link is set" },
      { name: "map_embed", label: "Google Maps embed link (optional, shows the exact pin)", type: "textarea", rows: 3, hint: "In Google Maps: search the office → Share → Embed a map → Copy HTML, then paste it here. The full <iframe> code or just the link both work. Leave empty to use the location above." },
      { name: "social_facebook", label: "Facebook URL" },
      { name: "social_instagram", label: "Instagram URL" },
      { name: "social_youtube", label: "YouTube URL" },
      { name: "social_twitter", label: "X / Twitter URL" },
      { name: "social_whatsapp", label: "WhatsApp number or link" },
    ],
  },
  affiliations: { label: "TNBA / BAI Affiliations & Quick Links", fields: [] },
};

const ListEditor = ({ items, onChange, keys, testid, extra }) => (
  <div className="space-y-4">
    {items.map((it, i) => (
      <div key={i} className="rounded-xl border border-slate-200 p-4 space-y-3 bg-white" data-testid={`${testid}-${i}`}>
        {extra?.(it, i)}
        {keys.map((k) => (
          <label key={k.name} className="block">
            <span className="block text-xs font-medium text-slate-600 mb-1">{k.label}</span>
            <FieldInput field={{ ...k, name: `${testid}-${i}-${k.name}` }} value={it[k.name]} onChange={(v) => onChange(items.map((x, j) => (j === i ? { ...x, [k.name]: v } : x)))} />
          </label>
        ))}
      </div>
    ))}
  </div>
);

const AffiliationsEditor = ({ form, setForm }) => (
  <div className="space-y-10">
    <div>
      <h3 className="font-display text-lg font-semibold text-[#0B1F44]">Affiliations</h3>
      <p className="text-sm text-slate-500 mb-4">Only affiliations marked as verified are shown on the public website.</p>
      <ListEditor testid="affiliation" items={form.affiliations} onChange={(v) => setForm({ ...form, affiliations: v })}
        keys={[{ name: "name", label: "Name" }, { name: "url", label: "Official website" }, { name: "logo", label: "Logo", type: "image" }, { name: "description", label: "Description" }]}
        extra={(it, i) => (
          <div className="flex items-center gap-3">
            <Switch checked={!!it.verified} onCheckedChange={(c) => setForm({ ...form, affiliations: form.affiliations.map((x, j) => (j === i ? { ...x, verified: c } : x)) })} data-testid={`affiliation-verified-${it.key}`} />
            <span className={`text-sm font-semibold ${it.verified ? "text-emerald-700" : "text-slate-500"}`}>{it.verified ? "Verified – shown publicly" : "Not verified – hidden"}</span>
          </div>
        )} />
    </div>
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display text-lg font-semibold text-[#0B1F44]">Quick links & player resources</h3>
        <button type="button" onClick={() => setForm({ ...form, quick_links: [...form.quick_links, { title: "", description: "", url: "" }] })} className="btn-outline text-sm" data-testid="quicklink-add"><Plus className="h-4 w-4" /> Add link</button>
      </div>
      <ListEditor testid="quicklink" items={form.quick_links} onChange={(v) => setForm({ ...form, quick_links: v })}
        keys={[{ name: "title", label: "Title" }, { name: "description", label: "Description" }, { name: "url", label: "URL" }]}
        extra={(_, i) => <button type="button" onClick={() => setForm({ ...form, quick_links: form.quick_links.filter((__, j) => j !== i) })} className="text-sm text-red-600 inline-flex items-center gap-1" data-testid={`quicklink-remove-${i}`}><Trash2 className="h-3.5 w-3.5" /> Remove</button>} />
    </div>
  </div>
);

export const SettingsEditor = ({ section }) => {
  const { settings, refreshSettings } = useSite();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (settings) setForm(settings); }, [settings]);
  if (!form) return null;
  const cfg = SETTINGS_SECTIONS[section];
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/settings", form);
      await refreshSettings();
      toast.success("Saved — changes are live on the website");
    } catch (err) {
      toast.error(fmtError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={save} data-testid={`admin-settings-${section}`}>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-2xl font-bold text-[#0B1F44]">{cfg.label}</h2>
        <button disabled={busy} className="btn-primary text-sm" data-testid="admin-settings-save">{busy ? "Saving..." : "Save changes"}</button>
      </div>
      {section === "affiliations" ? <AffiliationsEditor form={form} setForm={setForm} /> : (
        <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-5">
          {cfg.fields.map((f) => (
            <div key={f.name} className="block">
              <span className="block text-sm font-medium text-slate-700 mb-1.5">{f.label}</span>
              <FieldInput field={f} value={form[f.name]} onChange={(v) => setForm({ ...form, [f.name]: v })} />
            </div>
          ))}
        </div>
      )}
    </form>
  );
};
